import { Button } from 'components/atoms';
import { Form, Formik, FormikHelpers } from 'formik';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { useMutateOptions } from 'hooks';
import { useClientMode } from 'hooks/useClientMode';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiUser } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogin } from 'services/auth';
import * as Yup from 'yup';
import { isDevelopment } from 'services/environment/Environment';

type LoginFormModel = {
  email: string;
  password: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),
  password: Yup.string()
    .min(3, i18next.t('formik-error.invalid-password'))
    .required(i18next.t('formik-error.required')),
});

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const login = useLogin();
  const mutateOptions = useMutateOptions<LoginFormModel>();

  const onLogin = async (
    values: LoginFormModel,
    helpers: FormikHelpers<LoginFormModel>
  ) => {
    await login.mutateAsync(values, mutateOptions(helpers));
  };

  const navigateToResetPasswordRequest = (email: string) =>
    navigate(`/reset-password/request?email=${email}`);

  const navigateToResendConfirmEmail = (email: string) =>
    navigate(`/confirm-email/resend?email=${email}`);

  const clientModule = useClientMode();


  return (
    <Formik
      validateOnBlur
      validationSchema={validationSchema}
      initialValues={{
        email: params.get('email') ?? '',
        password: '',
      }}
      onSubmit={onLogin}>
      {({ values }) => (
        <PublicPage
          header={
            <>
              <PiUser />
              {t('login.header')}
            </>
          }
          footer={
            <p
              className='cursor-pointer p-2 w-full text-center'
              onClick={() => navigateToResendConfirmEmail(values.email)}>
              {t('login.resend-confirm-email')}
            </p>
          }>
          <Form>
            <FormInput.Text
              name='email'
              type='email'
              borderless
              appBackground
              label={t('input.label.email')}
            />
            <div className='flex flex-col gap-1 w-full'>
              <FormInput.Text
                name='password'
                type='password'
                borderless
                appBackground
                label={t('input.label.password')}
              />
              <p
                className='cursor-pointer w-full text-end text-sm p-2 pr-12 opacity-65'
                onClick={() => navigateToResetPasswordRequest(values.email)}>
                {t('login.forgot-password')}
              </p>
            </div>
            <Button
              type='submit'
              isLoading={login.isPending}
              variant={BUTTON_VARIANT.submit}
              fullWidth>
              <Button.Text text={t('login.button')} />
            </Button>
            {clientModule.isActive && !isDevelopment && (
              <div className='flex flex-col w-full gap-2 pt-8'>
                <p className='cursor-pointer p-2 w-full text-center text-sm opacity-65'>
                  Nie masz konta?
                </p>
                <Button
                  variant={BUTTON_VARIANT.background}
                  fullWidth
                  onClick={() => navigate('/register')}>
                  <Button.Text text={t('login.go-to-register')} />
                </Button>
              </div>
            )}
          </Form>
        </PublicPage>
      )}
    </Formik>
  );
};
export default Login;
